/**
 * React initialization module
 * This file must be imported first in the application entry point.
 * It ensures that React is properly initialized before any other code runs.
 * This helps prevent "Cannot read properties of undefined" errors in production.
 */

// Import React directly
import * as React from 'react';
import * as ReactDOM from 'react-dom';
import * as ReactDOMClient from 'react-dom/client';

// Define global types for React globals
declare global {
  interface Window {
    React: typeof React;
    ReactDOM: typeof ReactDOM;
    ReactDOMClient: typeof ReactDOMClient;
    __REACT_INIT_TIMESTAMP__: number;
    __DEBUG_REACT_CONTEXT__: boolean;
  }
}

// Log React availability
console.log('[react-init] Initializing React', {
  reactVersion: React?.version || 'undefined',
  reactAvailable: typeof React !== 'undefined',
  createContextAvailable: typeof React?.createContext === 'function'
});

// Create a global React instance to ensure it's available everywhere
if (typeof window !== 'undefined') {
  // Assign React to window to ensure it's globally available
  window.React = React;
  window.ReactDOM = ReactDOM;
  window.ReactDOMClient = ReactDOMClient;
  window.__REACT_INIT_TIMESTAMP__ = Date.now();
  window.__DEBUG_REACT_CONTEXT__ = true;
  
  // Test creating a context to verify React is working properly
  try {
    const TestContext = React.createContext(null);
    console.log('[react-init] Context creation test:', TestContext ? 'Success' : 'Failed');
  } catch (error) {
    console.error('[react-init] CRITICAL ERROR: Failed to create test context:', error);
  }
  
  console.log('[react-init] React initialized:', React.version);
}

export default React;
